import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { InputTextModule } from 'primeng/inputtext';

import { campaignModelDefinition } from '../../../../../../entity-definitions/campaign-model.definition';
import { BusinessRulesService } from '../../../../../../services/business-rules.service';
import { CampaignModelStore } from '../../../../../../stores/campaign-model.store';
import { CampaignModel } from '../../../../../../types/campaign.interface';

@Component({
  selector: 'app-save-campaign-model',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, FormsModule, InputTextModule, TranslocoModule],
  templateUrl: './save-campaign-model.component.html',
  styleUrl: './save-campaign-model.component.scss',
})
export class SaveCampaignModelComponent implements OnInit {
  private readonly dialogConfig = inject(DynamicDialogConfig);
  private readonly campaignModelStore = inject(CampaignModelStore);
  private readonly businessRulesService = inject(BusinessRulesService);
  private readonly translate = inject(TranslocoService);

  readonly modelName = signal('');
  readonly modelDescription = signal('');
  private readonly assetId = signal<number | null>(null);
  private readonly modelsLoaded = signal(false);

  readonly nameError = signal<string>('');
  readonly nameInputTouched = signal(false);

  readonly isValid = computed(() => {
    return this.modelName().trim().length > 0 && !this.hasNameError() && this.modelsLoaded();
  });

  hasNameError(): boolean {
    const error = this.nameError();
    return error !== null && error !== undefined && error.trim() !== '';
  }

  ngOnInit(): void {
    void this.initialize();
  }

  private async initialize(): Promise<void> {
    const dialogData = this.dialogConfig.data as { assetId?: number; campaignId?: number } | undefined;
    const assetId = dialogData?.assetId;

    if (assetId) {
      this.assetId.set(assetId);
      try {
        await this.campaignModelStore.getByAssetId(assetId);
        this.modelsLoaded.set(true);
      } catch (error) {
        console.error('Failed to load campaign models:', error);
        this.modelsLoaded.set(true);
      }
    } else {
      this.modelsLoaded.set(true);
    }
  }

  onModelNameChange(value: string) {
    this.modelName.set(value);
    this.validateName();
  }

  onModelNameBlur() {
    this.nameInputTouched.set(true);
    this.validateName();
  }

  validateName() {
    const trimmedName = this.modelName().trim();

    if (!trimmedName) {
      this.nameError.set(this.translate.translate('validation.required'));
      return;
    }

    if (trimmedName.length < 3) {
      this.nameError.set(this.translate.translate('validation.minLength', { min: 3 }));
      return;
    }

    if (trimmedName.length > 100) {
      this.nameError.set(this.translate.translate('validation.maxLength', { max: 100 }));
      return;
    }

    if (this.modelsLoaded()) {
      const dialogData = this.dialogConfig.data as { campaignId?: number } | undefined;
      const entityToValidate: Partial<CampaignModel> = {
        name: trimmedName,
        ...(dialogData?.campaignId !== undefined && { campaignId: dialogData.campaignId }),
      };

      const validationMessages = this.businessRulesService.getCreateValidationMessages(campaignModelDefinition, entityToValidate);

      const firstError = validationMessages[0];
      if (firstError) {
        this.nameError.set(firstError);
        return;
      }
    }

    this.nameError.set('');
  }
}
